/**
 * LivreJournal.jsx
 * Theme : fond blanc - accent #008fa0
 * Fix : background du header appliqué sur <th> directement (pas sur <thead>)
 */

import React, { useEffect, useMemo, useState } from 'react';
import { useGetLivreJournalQuery } from 'actions/comptabilite/queries';
import { exportLivreJournalExcel, exportLivreJournalPdf } from './utils/exportComptaExcel';
import {
  exportButtonGroupStyle,
  exportExcelButtonStyle,
  exportPdfButtonStyle,
} from './utils/exportButtonStyles';
import TableLoader from 'components/Loader/TableLoader';

// ── Google Fonts ─────────────────────────────────────────────────────
const injectFonts = () => {
  if (document.getElementById('lj-fonts')) return;
  const l = document.createElement('link');
  l.id   = 'lj-fonts';
  l.rel  = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&display=swap';
  document.head.appendChild(l);
};

// ── Tokens ───────────────────────────────────────────────────────────
const C = {
  bg:         '#f0f5f6',
  surface:    '#ffffff',
  border:     '#c8d8db',
  borderSoft: '#e2eced',
  teal:       'rgba(00,96,88,0.8)',
  tealLight:  'rgba(00,96,88,0.8)',
  tealDim:    'rgba(0,143,160,0.09)',
  green:      'rgba(00,96,88,0.8)',
  greenDim:   'rgba(26,122,82,0.09)',
  red:        '#c93535',
  redDim:     'rgba(201,53,53,0.09)',
  text:       '#0d2226',
  textDim:    '#3d6068',
  textMuted:  '#7da5ad',
  white:      '#ffffff',
  mono:       '"DM Mono", "Courier New", monospace',
  display:    '"Playfair Display", Georgia, serif',
  sans:       '"DM Sans", system-ui, sans-serif',
};

// ── Formatage ────────────────────────────────────────────────────────
const fmtMontant = (n) =>
  n && n !== 0 ? new Intl.NumberFormat('fr-FR').format(n) + '\u00a0F' : '';

// ── normalizeJournal ─────────────────────────────────────────────────
function normalizeJournal(raw) {
  const empty = { journal: [], total_debits: 0, total_credits: 0, equilibre: true };
  if (!raw) return empty;

  const total_debits  = raw.total_debits  ?? 0;
  const total_credits = raw.total_credits ?? 0;
  const equilibre     = raw.equilibre     ?? (total_debits === total_credits);

  if (!Array.isArray(raw.journal) || raw.journal.length === 0)
    return { journal: [], total_debits, total_credits, equilibre };

  const first       = raw.journal[0] ?? {};
  const isNewFormat = Array.isArray(first.lignes);

  if (isNewFormat) {
    return {
      journal: raw.journal.map((e, i) => ({
        numero_ecriture: e.numero_ecriture ?? i + 1,
        date:            e.date            ?? '',
        libelle_global:  e.libelle_global  ?? e.libelle ?? '',
        piece_ref:       e.piece_ref       ?? '',
        journal_label:   e.journal_label   ?? '',
        lignes:          Array.isArray(e.lignes) ? e.lignes : [],
      })),
      total_debits, total_credits, equilibre,
    };
  }

  // Ancien format plat
  return {
    journal: raw.journal.map((e, i) => ({
      numero_ecriture: i + 1,
      date:            e.date    ?? '',
      libelle_global:  e.libelle ?? '',
      lignes: [
        { type:'debit',  code: e.code_debit,  intitule: 'Cpte ' + (e.code_debit  ?? '?'), debit: e.debit  ?? 0, credit: 0,           indent: false },
        { type:'credit', code: e.code_credit, intitule: 'Cpte ' + (e.code_credit ?? '?'), debit: 0,            credit: e.credit ?? 0, indent: true  },
      ],
    })),
    total_debits, total_credits, equilibre,
  };
}

// ── Style du <th> - background directement sur la cellule ────────────
const thStyle = (align = 'left') => ({
  fontFamily:      '"DM Mono", "Courier New", monospace',
  fontSize:        '11px',
  fontWeight:      600,
  letterSpacing:   '1.5px',
  textTransform:   'uppercase',
  color:           '#0c0a0a',
  backgroundColor: '#008fa0',          // ← directement sur th
  padding:         '12px 14px',
  textAlign:       align,
  borderBottom:    '2px solid #006b78',
  whiteSpace:      'nowrap',
});

// ── Styles ───────────────────────────────────────────────────────────
const S = {
  wrapper: {
    fontFamily: C.sans,
    background: C.bg,
    color:      C.text,
    padding:    '28px 20px 60px',
    minHeight:  '100vh',
  },
  inner: { maxWidth: '1080px', margin: '0 auto' },

  headerRow: { display:'flex', alignItems:'center', gap:'14px', marginBottom:'6px' },
  title: { fontFamily:C.display, fontSize:'1.6rem', fontWeight:700, color:C.text, margin:0 },
  badge: {
    fontFamily:'DM Mono, monospace', fontSize:'9px', letterSpacing:'2px', textTransform:'uppercase',
    color:C.teal, background:C.tealDim,
    border:'1px solid rgba(0,143,160,0.25)',
    padding:'3px 12px', borderRadius:'100px',
  },
  divider: {
    height:'1px',
    background:'linear-gradient(90deg, ' + C.border + ', transparent)',
    marginTop:'8px', marginBottom:'18px',
  },

  equilibre: (ok) => ({
    display:'inline-flex', alignItems:'center', gap:'8px',
    fontFamily:'DM Mono, monospace', fontSize:'11px', letterSpacing:'1px',
    color:      ok ? C.green : C.red,
    background: ok ? C.greenDim : C.redDim,
    border:     '1px solid ' + (ok ? 'rgba(26,122,82,0.3)' : 'rgba(201,53,53,0.3)'),
    padding:'5px 14px', borderRadius:'100px', marginBottom:'14px',
  }),

  legend: {
    display:'flex', gap:'20px', justifyContent:'flex-end',
    marginBottom:'10px', fontSize:'11px',
    fontFamily:'DM Mono, monospace', color:C.textDim,
  },
  dot: (color) => ({
    width:8, height:8, borderRadius:'50%',
    background:color, display:'inline-block', marginRight:'5px',
  }),

  card: {
    background:   C.surface,
    border:       '1px solid ' + C.border,
    borderRadius: '10px',
    overflow:     'hidden',
    boxShadow:    '0 2px 20px rgba(0,143,160,0.08), 0 1px 4px rgba(0,0,0,0.05)',
  },
  tableWrap: {
    overflowX: 'auto',
    WebkitOverflowScrolling: 'touch',
  },
  table: { width:'100%', minWidth:'900px', borderCollapse:'collapse', fontSize:'13px' },

  TD: (extra = {}) => ({
    padding:      '9px 14px',
    verticalAlign:'middle',
    lineHeight:   1.4,
    borderBottom: '1px solid ' + C.borderSoft,
    ...extra,
  }),

  sep: {
    background:   'linear-gradient(90deg, rgba(0,143,160,0.07), rgba(0,143,160,0.02) 60%, transparent)',
    borderTop:    '1px dashed rgba(0,143,160,0.22)',
  },
  sepTd: {
    padding:      '5px 14px',
    fontFamily:   'DM Mono, monospace',
    fontSize:     '9px',
    letterSpacing:'1.5px',
    color:        C.teal,
    borderBottom: 'none',
  },

  totalRow:   { background:'rgba(0,143,160,0.06)', borderTop:'2px solid rgba(0,143,160,0.2)' },
  totalLabel: { padding:'12px 14px', fontFamily:'DM Mono, monospace', fontSize:'10px', letterSpacing:'1px', color:C.teal },
  totalAmt:   { padding:'12px 14px', fontFamily:'DM Mono, monospace', fontSize:'13px', fontWeight:700, color:C.teal, textAlign:'right' },

  amtD:   { fontFamily:'DM Mono, monospace', fontWeight:600, color:C.green, fontSize:'13px' },
  amtC:   { fontFamily:'DM Mono, monospace', fontWeight:600, color:C.red,   fontSize:'13px' },
  dash:   { fontFamily:'DM Mono, monospace', color:C.textMuted, fontSize:'12px' },
  codeD:  { fontFamily:'DM Mono, monospace', fontSize:'12px', color:C.teal,    fontWeight:600 },
  codeC:  { fontFamily:'DM Mono, monospace', fontSize:'12px', color:C.textDim, fontWeight:400 },
  intD:   { color:C.text,    fontSize:'13px' },
  intC:   { color:C.textDim, fontSize:'13px', fontStyle:'italic', paddingLeft:'22px', display:'inline-block' },

  empty:  { textAlign:'center', color:C.textMuted, fontFamily:'DM Mono, monospace', fontSize:'12px', padding:'50px', letterSpacing:'1px' },
  loader: { textAlign:'center', padding:'60px', fontFamily:'DM Mono, monospace', fontSize:'12px', color:C.textMuted, letterSpacing:'2px' },
  searchBar: {
    display: 'flex',
    gap: '8px',
    alignItems: 'center',
    marginBottom: '12px',
  },
  searchInput: {
    width: '100%',
    border: '1px solid ' + C.border,
    borderRadius: '6px',
    padding: '8px 10px',
    fontFamily: C.sans,
    fontSize: '13px',
    outline: 'none',
  },
  pagination: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginTop: '12px',
    gap: '8px',
    flexWrap: 'wrap',
  },
  pageBtn: {
    border: '1px solid ' + C.border,
    background: C.surface,
    borderRadius: '6px',
    padding: '6px 10px',
    cursor: 'pointer',
    fontFamily: C.mono,
    fontSize: '11px',
  },
};

// ── Ligne d'écriture ─────────────────────────────────────────────────
function LigneCpt({ ligne, date, showDate }) {
  const [hov, setHov] = useState(false);
  if (!ligne) return null;
  const isDebit = ligne.type === 'debit';

  return (
    <tr
      style={{ background: hov ? 'rgba(0,143,160,0.03)' : C.surface, transition:'background .15s' }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* Date */}
      <td style={S.TD({ fontFamily:'DM Mono, monospace', fontSize:'11px', color:C.textDim, whiteSpace:'nowrap', width:'105px' })}>
        {showDate ? date : ''}
      </td>

      {/* N° compte */}
      <td style={S.TD({ width:'95px' })}>
        <span style={isDebit ? S.codeD : S.codeC}>
          {ligne.code || '-'}
        </span>
      </td>

      {/* Intitulé */}
      <td style={S.TD({})}>
        {isDebit
          ? <span style={S.intD}>{ligne.intitule || '-'}</span>
          : <span style={S.intC}>{ligne.intitule || '-'}</span>
        }
      </td>

      {/* Débit */}
      <td style={S.TD({ textAlign:'right', width:'145px' })}>
        {isDebit && ligne.debit
          ? <span style={S.amtD}>{fmtMontant(ligne.debit)}</span>
          : <span style={S.dash}>-</span>
        }
      </td>

      {/* Crédit */}
      <td style={S.TD({ textAlign:'right', width:'145px' })}>
        {!isDebit && ligne.credit
          ? <span style={S.amtC}>{fmtMontant(ligne.credit)}</span>
          : <span style={S.dash}>-</span>
        }
      </td>
    </tr>
  );
}

// ── Composant principal ──────────────────────────────────────────────
const JOURNAL_FILTERS = [
  { code: '',          label: 'Tous les journaux' },
  { code: 'OD',        label: 'Opérations diverses' },
  { code: 'ACHATS',    label: 'Achats' },
  { code: 'VENTES',    label: 'Ventes' },
  { code: 'BANQUE',    label: 'Banque' },
  { code: 'CAISSE',    label: 'Caisse' },
  { code: 'SALAIRES',  label: 'Salaires' },
  { code: 'FISCALITE', label: 'Fiscalité' },
];

const LivreJournal = ({ dossier_id, date_debut, date_fin }) => {
  useEffect(() => { injectFonts(); }, []);
  const [searchTerm, setSearchTerm] = useState('');
  const [journalFilter, setJournalFilter] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const PAGE_SIZE = 12;

  const { data: raw, isLoading, isFetching, refetch } = useGetLivreJournalQuery(
    { dossier_id, date_debut, date_fin, journal: journalFilter || undefined },
    { skip: !dossier_id }
  );

  useEffect(() => {
    if (dossier_id) refetch();
  }, [dossier_id, date_debut, date_fin, refetch]);
  useEffect(() => {
    const handleRefresh = () => refetch();
    window.addEventListener("compta:refresh", handleRefresh);
    return () => window.removeEventListener("compta:refresh", handleRefresh);
  }, [refetch]);

  const { journal, total_debits, total_credits, equilibre } = normalizeJournal(raw);
  const filteredJournal = useMemo(() => {
    const q = searchTerm.trim().toLowerCase();
    if (!q) return journal;
    return journal.filter((ecriture) => {
      const inHeader = (
        String(ecriture.numero_ecriture || '').toLowerCase().includes(q)
        || String(ecriture.date || '').toLowerCase().includes(q)
        || String(ecriture.libelle_global || '').toLowerCase().includes(q)
      );
      if (inHeader) return true;
      return (ecriture.lignes || []).some((l) => (
        String(l.code || '').toLowerCase().includes(q)
        || String(l.intitule || '').toLowerCase().includes(q)
      ));
    });
  }, [journal, searchTerm]);

  const totalPages = Math.max(1, Math.ceil(filteredJournal.length / PAGE_SIZE));
  const paginatedJournal = useMemo(() => {
    const start = (currentPage - 1) * PAGE_SIZE;
    return filteredJournal.slice(start, start + PAGE_SIZE);
  }, [filteredJournal, currentPage]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchTerm, journal.length]);

  useEffect(() => {
    if (currentPage > totalPages) setCurrentPage(totalPages);
  }, [currentPage, totalPages]);

  if (isLoading || isFetching) {
    return (
      <div style={S.wrapper}>
        <div style={S.inner}>
          <div style={{ ...S.card, padding: '30px' }}>
            <TableLoader
              message="Chargement du livre journal..."
              subMessage="Récupération et formatage des écritures comptables"
              minHeight="350px"
            />
          </div>
        </div>
      </div>
    );
  }

  const handleExportExcel = () => {
    exportLivreJournalExcel({
      journal,
      totalDebits: total_debits,
      totalCredits: total_credits,
      equilibre,
      dossierId: dossier_id,
    });
  };
  const handleExportPdf = () => {
    exportLivreJournalPdf({
      journal,
      totalDebits: total_debits,
      totalCredits: total_credits,
      equilibre,
      dossierId: dossier_id,
    });
  };

  return (
    <div style={S.wrapper}>
      <div style={S.inner}>

        {/* Titre */}
        <div style={S.headerRow} className="compta-header-row">
          <div className="compta-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <h2 style={S.title}>Journal</h2>
            <span style={S.badge}>JOURNAL GÉNÉRAL</span>
          </div>
          <div style={exportButtonGroupStyle} className="compta-export-group">
            <button type="button" onClick={handleExportExcel} style={exportExcelButtonStyle}>
              Export Excel
            </button>
            <button type="button" onClick={handleExportPdf} style={exportPdfButtonStyle}>
              Export PDF
            </button>
          </div>
        </div>
        <div style={S.divider} />

        {/* Équilibre */}
        {journal.length > 0 && (
          <div style={S.equilibre(equilibre)}>
            <span>{equilibre ? '✓' : '✗'}</span>
            {equilibre ? 'Partie double équilibrée' : 'DÉSÉQUILIBRE - vérifiez les écritures'}
            <span style={{ color:C.textMuted, marginLeft:'6px' }}>
              {new Intl.NumberFormat('fr-FR').format(total_debits) + ' F  /  ' + new Intl.NumberFormat('fr-FR').format(total_credits) + ' F'}
            </span>
          </div>
        )}

        {/* Légende */}
        <div style={S.legend}>
          <span><span style={S.dot(C.green)} />Débit</span>
          <span><span style={S.dot(C.red)}   />Crédit</span>
        </div>

        <div style={S.searchBar}>
          <input
            style={S.searchInput}
            type="text"
            placeholder="Rechercher (date, libellé, compte, intitulé...)"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
          />
          <select
            value={journalFilter}
            onChange={(e) => { setJournalFilter(e.target.value); setCurrentPage(1); }}
            style={{
              border: '1px solid ' + C.border, borderRadius: '6px',
              padding: '8px 10px', fontFamily: C.sans, fontSize: '12px',
              minWidth: '170px', background: C.surface,
            }}
          >
            {JOURNAL_FILTERS.map((j) => (
              <option key={j.code} value={j.code}>{j.label}</option>
            ))}
          </select>
        </div>

        {/* Tableau */}
        <div style={S.card}>
          <div style={S.tableWrap}>
          <table style={S.table}>

            {/* ── HEADERS : background directement sur chaque <th> ── */}
            <thead>
              <tr>
                <th style={thStyle('left')}>Date</th>
                <th style={thStyle('left')}>N° Compte</th>
                <th style={thStyle('left')}>Intitulé du compte</th>
                <th style={thStyle('right')}>Débit (FCFA)</th>
                <th style={thStyle('right')}>Crédit (FCFA)</th>
              </tr>
            </thead>

            <tbody>
              {filteredJournal.length === 0 ? (
                <tr>
                  <td colSpan={5} style={S.empty}>AUCUNE ÉCRITURE COMPTABLE</td>
                </tr>
              ) : (
                paginatedJournal.map((ecriture) => (
                  <React.Fragment key={'e-' + ecriture.numero_ecriture}>

                    {/* Séparateur */}
                    <tr style={S.sep}>
                      <td colSpan={5} style={S.sepTd}>
                        {'>> Écriture N°' + ecriture.numero_ecriture}
                        {ecriture.piece_ref       ? '  ·  ' + ecriture.piece_ref       : ''}
                        {ecriture.journal_label   ? '  ·  ' + ecriture.journal_label   : ''}
                        {ecriture.date           ? '  ·  ' + ecriture.date           : ''}
                        {ecriture.libelle_global  ? '  ·  ' + ecriture.libelle_global  : ''}
                      </td>
                    </tr>

                    {/* Lignes débit / crédit */}
                    {Array.isArray(ecriture.lignes) && ecriture.lignes.map((ligne, li) => (
                      <LigneCpt
                        key={'l-' + ecriture.numero_ecriture + '-' + li}
                        ligne={ligne}
                        date={ecriture.date}
                        showDate={li === 0}
                      />
                    ))}

                  </React.Fragment>
                ))
              )}

              {/* Totaux */}
              {filteredJournal.length > 0 && (
                <tr style={S.totalRow}>
                  <td colSpan={3} style={S.totalLabel}>
                    TOTAUX GÉNÉRAUX &nbsp;-&nbsp; {filteredJournal.length} écriture{filteredJournal.length > 1 ? 's' : ''}
                  </td>
                  <td style={S.totalAmt}>{new Intl.NumberFormat('fr-FR').format(total_debits)} F</td>
                  <td style={S.totalAmt}>{new Intl.NumberFormat('fr-FR').format(total_credits)} F</td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        <div style={S.pagination}>
          <span style={{ fontFamily: C.mono, fontSize: '11px', color: C.textDim }}>
            Page {currentPage} / {totalPages}
          </span>
          <div style={{ display: 'flex', gap: '8px' }}>
            <button
              type="button"
              style={S.pageBtn}
              disabled={currentPage <= 1}
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
            >
              Précédent
            </button>
            <button
              type="button"
              style={S.pageBtn}
              disabled={currentPage >= totalPages}
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
            >
              Suivant
            </button>
          </div>
        </div>

      </div>
    </div>
  );
};

export default LivreJournal;